import { getCleanName } from "@/shared/utils/accountConfig";

export function formatActivityDateTime(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getEventLabel(eventKey, channel = "") {
  switch (eventKey) {
    case "sentAt":
      return channel === "email" ? "Email sent" : "Message sent";
    case "openedAt":
      return "Email opened";
    case "linkClickedAt":
      return "Email link clicked";
    case "repliedAt":
      if (channel === "email") return "Email replied";
      if (channel === "whatsapp") return "WhatsApp message replied";
      return "LinkedIn message replied";
    case "connectionRequestSentAt":
      return "LinkedIn connection request sent";
    case "connectionRequestAcceptedAt":
      return "LinkedIn connection request accepted";
    case "messageSentAt":
      return channel === "whatsapp"
        ? "WhatsApp message sent"
        : "LinkedIn message sent";
    case "messageReadAt":
      return channel === "whatsapp"
        ? "WhatsApp message read"
        : "LinkedIn message read";
    case "postLikedAt":
      return "LinkedIn post liked";
    default:
      return "Activity recorded";
  }
}

function getEventDescription(eventKey) {
  switch (eventKey) {
    case "openedAt":
      return "Try to move towards a meeting or demo";
    case "linkClickedAt":
      return "High intent — consider a timely follow-up";
    case "repliedAt":
      return "Review the reply and plan your next step";
    case "connectionRequestAcceptedAt":
      return "Connection accepted — send a follow-up message";
    case "messageReadAt":
      return "Message was read — wait or send a gentle nudge";
    case "postLikedAt":
      return "Post liked — continue with the next sequence step";
    default:
      return null;
  }
}

export function buildCampaignActivityTimeline(
  campaignActivities = [],
  contactName = "",
) {
  const displayName = getCleanName(contactName) || "Contact";
  const enrollmentItems = [];
  const eventItems = [];

  for (const campaign of campaignActivities) {
    const {
      campaignId,
      campaignName,
      enrollmentStatus,
      enrolledAt,
      activities = [],
    } = campaign;

    if (
      String(enrollmentStatus || "").toUpperCase() === "ACTIVE" &&
      enrolledAt
    ) {
      enrollmentItems.push({
        id: `enrollment-${campaignId}`,
        type: "enrollment",
        channel: "campaign",
        timestamp: enrolledAt,
        title: `${displayName} enrolled in ${campaignName || "campaign"}`,
        description: `at ${formatActivityDateTime(enrolledAt)}`,
        campaignId,
      });
    }

    for (const activity of activities) {
      const channel = activity.channel || "";

      for (const [eventKey, timestamp] of Object.entries(
        activity.events || {},
      )) {
        if (!timestamp) continue;

        const label = getEventLabel(eventKey, channel);

        eventItems.push({
          id: `${activity.stepId}-${eventKey}`,
          type: "event",
          channel,
          timestamp,
          title: `${displayName} — ${label}`,
          description: `at ${formatActivityDateTime(timestamp)}`,
          hint: getEventDescription(eventKey),
          campaignId,
          stepOrder: activity.stepOrder,
        });
      }
    }
  }

  enrollmentItems.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  eventItems.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  return [...enrollmentItems, ...eventItems];
}
